// Finding a defect in the quality taxonomy from what an operator typed.
//
// The taxonomy is Odoo's `quality.alert.category`: two levels, ~330 rows, the
// office's words ("Astron scratched", "Specs , size incorrect"). The floor types
// its own words, on a phone, often by voice, and the old search was a plain
// substring — "chipping" did not find "Chipped", "tiles" did not find "Tiling",
// one wrong letter found nothing. A search that finds nothing sends the
// operator to the wrong defect or away from the alert altogether, so this one
// forgives:
//
//   * word ending   chips / chipped / chipping all meet at "chip"
//   * part words    "astronaut" still finds "Astron"; "scra" finds "Scratched"
//   * one slip      "cracekd", "waterprofing" — one edit, two on a long word
//   * word order    every word is looked for on its own
//
// Every typed word that matches counts, and the rows that match the MOST of
// them are the ones shown: "chipped benchtop" keeps to benchtop chips, but
// "chipped astronaut" — which no row names both halves of — still offers the
// chips and the Astron rows rather than nothing.
//
// And when it is genuinely not there, the taxonomy has its own escape hatch:
// most categories end in "[x] Other", and a top-level "[x] Other" holds "Free
// text". `otherFor` finds the right one so the sheet can offer it with the
// typed words carried into the alert's description (28 Oct 2026 systems
// meeting). Found by name, never by id, so it holds on the labs clone and on
// production alike.

export interface DefectRow {
  id: number;
  name: string;
  parentId: number | null;
}

export interface DefectLeaf extends DefectRow {
  parentName: string;
}

/** Words that carry no meaning in a defect name and would match everything. */
const STOP = new Set(['a', 'an', 'the', 'of', 'on', 'in', 'to', 'and', 'or', 'is', 'at', 'for', 'with', 'it', 'its', 'x']);

/** Lower case, `[x]` and punctuation gone, split to words. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/\[x\]/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w !== '');
}

function queryWords(query: string): string[] {
  return words(query).filter((w) => !STOP.has(w));
}

/**
 * A rough stem: enough that the endings the floor actually uses meet. Not a
 * linguist's stemmer — "tiling" comes out "til", which is why stems are then
 * compared by prefix rather than for equality.
 */
export function stem(word: string): string {
  let w = word;
  if (w.length > 4 && /(ss|x|z|ch|sh)es$/.test(w)) w = w.slice(0, -2);
  else if (w.length > 4 && w.endsWith('ing')) w = w.slice(0, -3);
  else if (w.length > 3 && w.endsWith('ed')) w = w.slice(0, -2);
  else if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) w = w.slice(0, -1);
  // chipp → chip, cutt → cut: the doubled consonant before -ed / -ing.
  if (w.length > 3 && w !== word && w[w.length - 1] === w[w.length - 2] && !/[aeiou]/.test(w[w.length - 1]!)) {
    w = w.slice(0, -1);
  }
  return w;
}

/** Edit distance with adjacent swaps counted as one, bailing out past `max`. */
export function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2 = new Array<number>(b.length + 1).fill(0);
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2]! + 1);
      cur[j] = v;
      rowMin = Math.min(rowMin, v);
    }
    if (rowMin > max) return max + 1;
    prev2.splice(0, prev2.length, ...prev);
    prev = cur;
  }
  return prev[b.length]!;
}

/** How well one typed word matches one word of a name: 0 is not at all. */
export function wordScore(typed: string, word: string): number {
  if (word === typed) return 4;
  if (typed.length >= 2 && word.startsWith(typed)) return 3;
  const a = stem(typed);
  const b = stem(word);
  if (a === b) return 3;
  if (Math.min(a.length, b.length) >= 3 && (a.startsWith(b) || b.startsWith(a))) return 2;
  if (typed.length >= 3 && word.includes(typed)) return 2;
  if (typed.length >= 4) {
    const allowed = typed.length >= 8 ? 2 : 1;
    if (distance(typed, word, allowed) <= allowed) return 1;
    // Still typing, with a slip in it: "scrat" against the start of "scratched".
    if (word.length > typed.length && distance(typed, word.slice(0, typed.length), 1) <= 1) return 1;
  }
  return 0;
}

interface Scored<T> {
  row: T;
  matched: number;
  score: number;
}

function scoreAll<T>(rows: readonly T[], typed: string[], text: (r: T) => string): Scored<T>[] {
  return rows.map((row) => {
    const ws = words(text(row));
    let matched = 0;
    let score = 0;
    for (const q of typed) {
      let best = 0;
      for (const w of ws) best = Math.max(best, wordScore(q, w));
      if (best > 0) {
        matched += 1;
        score += best;
      }
    }
    return { row, matched, score };
  });
}

/**
 * The categories and defects a query finds, best first.
 *
 * Categories are matched on their own name, defects on their category's name
 * and their own, so "tile" still brings up the Tiling category and every
 * defect under it. Only rows matching the most typed words survive; within
 * them, the closer match leads.
 */
export function searchDefects<P extends DefectRow, L extends DefectLeaf>(
  parents: readonly P[],
  leaves: readonly L[],
  query: string,
  leafLimit = 12,
): { parents: P[]; leaves: L[] } {
  const typed = queryWords(query);
  if (typed.length === 0) return { parents: [], leaves: [] };
  const p = scoreAll(parents, typed, (r) => r.name);
  const l = scoreAll(leaves, typed, (r) => `${r.parentName} ${r.name}`);
  const most = Math.max(0, ...p.map((s) => s.matched), ...l.map((s) => s.matched));
  if (most === 0) return { parents: [], leaves: [] };
  // Ties keep the taxonomy's own order (the sort is stable), which is what
  // keeps an Other below the real defects it ties with.
  const keep = <T extends DefectRow>(s: Scored<T>[]) =>
    s
      .filter((x) => x.matched === most)
      .sort((x, y) => y.score - x.score)
      .map((x) => x.row);
  return { parents: keep(p), leaves: keep(l).slice(0, leafLimit) };
}

/**
 * A defect name as the floor should read it.
 *
 * The office prefixes a row with `[x] ` for one reason: Odoo orders the
 * taxonomy by name, and the prefix sorts "[x] Other" below every real defect.
 * That is a sorting trick, not a word, so it comes off wherever a name is
 * shown — the category list, an alert's category, and the titles Odoo builds
 * from them ("[x] Other - Free text"). The order it bought is kept separately
 * (`sortsLast`).
 */
export function defectDisplay(text: string): string {
  return text.replace(/\[x\]\s*/gi, '');
}

/** Whether the office has marked this row to sit at the bottom of its list. */
export function sortsLast(rawName: string): boolean {
  return /^\s*\[x\]/i.test(rawName);
}

/** "Other", "[x] Other" — the taxonomy's own catch-all, by name. Not "(No SKU)". */
export function isOther(name: string): boolean {
  return /^\s*(\[x\]\s*)?other\s*$/i.test(name);
}

/**
 * Where an unlisted problem goes.
 *
 * Inside a category, that category's own Other, so the alert still lands in
 * the right area. Otherwise — or in a category with no Other of its own — the
 * top-level Other's "Free text", falling back to its own Other. Null where the
 * taxonomy has neither, and the sheet then offers nothing it cannot raise.
 */
export function otherFor<P extends DefectRow, L extends DefectLeaf>(
  parents: readonly P[],
  leaves: readonly L[],
  categoryId: number | null,
): L | null {
  if (categoryId != null) {
    const own = leaves.find((l) => l.parentId === categoryId && isOther(l.name));
    if (own) return own;
  }
  const top = parents.find((p) => isOther(p.name));
  if (!top) return null;
  const under = leaves.filter((l) => l.parentId === top.id);
  return under.find((l) => /^\s*free\s*text\s*$/i.test(l.name)) ?? under.find((l) => isOther(l.name)) ?? null;
}

/**
 * The description an Other alert opens with: the words the operator already
 * typed, ahead of anything they had written there.
 */
export function withTypedWords(typed: string, notes: string): string {
  const words = typed.trim();
  if (!words) return notes;
  const rest = notes.trim();
  if (rest.toLowerCase().startsWith(words.toLowerCase())) return notes;
  return rest ? `${words}\n\n${rest}` : words;
}
